import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Production-safe token storage. Some hosting environments (Kubernetes
// ingress, CDN) strip the Set-Cookie header on cross-host responses, so
// we ALSO persist the access_token here and attach it as a Bearer header
// (see the interceptor in index.js).
const TOKEN_KEY = 'trv_access_token';
const REFRESH_KEY = 'trv_refresh_token';

export const setStoredTokens = (access, refresh) => {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
};
export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
export const clearStoredTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);       // null = checking, false = not auth'd, object = auth'd
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (err) {
      // Auth check expected to fail when not logged in
      setUser(false);
      clearStoredTokens();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password }, { withCredentials: true });
    setStoredTokens(data?.access_token, data?.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (name, email, password, phone) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/register`, { name, email, password, phone }, { withCredentials: true });
    // Self-signups are pending admin approval — no session is issued.
    if (data?.pending) return data;
    setStoredTokens(data?.access_token, data?.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) { console.debug('Logout cleanup skipped:', err?.message || err); }
    clearStoredTokens();
    setUser(false);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/google-session`, { session_id: sessionId }, { withCredentials: true });
    setStoredTokens(data?.access_token, data?.refresh_token);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, processGoogleSession, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
